import ListItem from "@mui/material/ListItem";
import { useTranslation } from "react-i18next";
import useSidebar from "../../SidebarProvider/useSidebar";
import { memo, useCallback, useMemo } from "react";
import TooltipHelper from "components/TooltipHelper";
import Typography from "components/Typography";
import RouteLink from "./components/RouteLink";
import { intercomTarget } from "config/intercom-targets";
import LockIcon from "components/Icons/LockIcon";
import { styled } from "@mui/material";
import AnalyticsService from "services/AnalyticsService";
import useSubscription from "hooks/useSubscription";
import HotspotBadge from "components/HotspotBadge";
import type { SidebarUpsell } from "../../hooks/useSidebarRouteList/useSidebarRouteList";

const LockedItem = styled("span")({
  padding: "4px 8px",
  gap: "14px",
  display: "flex",
  alignItems: "center",
  minHeight: "28px",
  borderRadius: "4px",
  cursor: "default",
  color: "white",
  textDecoration: "none",
  width: "100%",
  opacity: 0.4,
});

const LEGACY_PLAN_TYPE = "Legacy";

/**
 * A locked upsell entry stays clickable — it routes to the upsell landing page —
 * so it keeps the link affordance but is dimmed and carries a lock icon.
 *
 * Icon sizing lives here rather than in `sx` props so the upsell branch does not
 * allocate style objects on every render.
 */
const UpsellItemLink = styled(RouteLink)({
  opacity: 0.6,

  "&:hover": {
    opacity: 1,
  },

  ".MuiSvgIcon-root": {
    color: "#FFF",
    fontSize: "20px",
  },

  "[data-upsell-lock]": {
    fontSize: "16px",
    marginLeft: "-10px",
  },
});

interface SidebarRouteItemProps {
  route: string;
  icon: React.ElementType;
  title: string;
  locked?: boolean;
  upsell?: SidebarUpsell;
  hotspot?: boolean;
}

function SidebarRouteItem(props: SidebarRouteItemProps) {
  const { route, icon, title, locked, upsell, hotspot } = props;

  const { t } = useTranslation();
  const { open } = useSidebar();
  const subscription = useSubscription();

  const upsellFeature = upsell?.feature;
  /** Legacy orgs have no subscription; report them as a tier rather than dropping them. */
  const planType = subscription?.planType ?? LEGACY_PLAN_TYPE;

  /** Keyed on the two reported values rather than the whole upsell and
   * subscription objects, so the handler identity is as stable as the data. */
  const handleUpsellClick = useCallback(() => {
    if (!upsellFeature) return;

    AnalyticsService.logEvent(AnalyticsService.EVENTS.UPSELL_LOCKED_FEATURE_CLICKED, {
      [AnalyticsService.PROPERTIES.UPSELL_FEATURE]: upsellFeature,
      [AnalyticsService.PROPERTIES.UPSELL_PLAN_TYPE]: planType,
    });
  }, [upsellFeature, planType]);

  const Icon = useMemo(() => icon, [icon]);

  const intercomTargetValue = useMemo(() => intercomTarget.sidebarNav(route.replace("/", "")), [route]);

  if (upsell) {
    return (
      <TooltipHelper placement="right" title={open ? t(upsell.tooltip) : t(title)}>
        <ListItem disablePadding data-intercom-target={intercomTargetValue}>
          <UpsellItemLink to={route} aria-label={t(title)} onClick={handleUpsellClick}>
            <Icon />
            {open ? (
              <>
                <Typography variant="TextS" noWrap>
                  {t(title)}
                </Typography>
                <LockIcon data-upsell-lock />
              </>
            ) : null}
          </UpsellItemLink>
        </ListItem>
      </TooltipHelper>
    );
  }

  if (locked) {
    return (
      <TooltipHelper placement="right" title={open ? null : t(title)}>
        <ListItem disablePadding data-intercom-target={intercomTargetValue}>
          <LockedItem aria-disabled="true">
            <Icon sx={{ color: "#FFF", fontSize: "20px" }} />
            {open ? (
              <>
                <Typography variant="TextS" noWrap>
                  {t(title)}
                </Typography>
                <LockIcon sx={{ color: "#FFF", fontSize: "16px", marginLeft: "-10px" }} />
              </>
            ) : null}
          </LockedItem>
        </ListItem>
      </TooltipHelper>
    );
  }

  return (
    <TooltipHelper placement="right" title={open ? null : t(title)}>
      <ListItem disablePadding data-intercom-target={intercomTargetValue}>
        <RouteLink to={route} aria-label={t(title)}>
          <HotspotBadge variant="dot" invisible={!hotspot}>
            <Icon sx={{ color: "#FFF", fontSize: "20px" }} />
          </HotspotBadge>
          {open ? (
            <Typography variant="TextS" noWrap>
              {t(title)}
            </Typography>
          ) : null}
        </RouteLink>
      </ListItem>
    </TooltipHelper>
  );
}

export default memo(SidebarRouteItem);
